import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ContentChild,
  ContentChildren,
  QueryList,
} from '@angular/core';
import { MatPaginator, MatPaginatorSelectConfig, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ColumnConfig, PaginationConfig } from './column-config.model';

@Component({
  selector: 'app-reusable-table',
  templateUrl: './reusable-table.component.html',
  styleUrls: ['./reusable-table.component.scss'],
  standalone: false,
})
export class ReusableTableComponent implements AfterViewInit, OnChanges {

  /* ───────────── Inputs ───────────── */

  /** Column definitions */
  @Input() columns: ColumnConfig[] = [];

  /** Row data array */
  @Input() data: any[] = [];

  /** If true, rows are clickable and emit rowClick */
  @Input() clickableRows: boolean = false;

  /** Server-side pagination config. If null, client-side pagination is used. */
  @Input() pagination: PaginationConfig | null = null;

  /** Enable/disable sorting. Default true */
  @Input() sortable: boolean = true;

  /** Show/hide paginator. Default true */
  @Input() showPaginator: boolean = true;

  /** Custom CSS class applied to the table host */
  @Input() tableClass: string = '';

  /** Row height in pixels. Default  */
  @Input() rowHeight: number = 36;

  /** Sticky header. Default true */
  @Input() stickyHeader: boolean = true;

  /** Currently selected/highlighted row (for external highlight control) */
  @Input() selectedRow: any = null;

  /** Field to compare for row selection highlight */
  @Input() selectionKey: string = '';

  /** Custom cell templates passed via content projection */
  @Input() cellTemplates: { [key: string]: TemplateRef<any> } = {};

  /** Configuration for the paginator's internal mat-select (e.g. panelClass) */
  @Input() paginatorSelectConfig: MatPaginatorSelectConfig = {};

  /* ───────────── Outputs ───────────── */

  @Output() rowClick = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() sortChange = new EventEmitter<Sort>();

  /* ───────────── ViewChildren ───────────── */

  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort!: MatSort;

  /* ───────────── Internal state ───────────── */

  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  displayedColumns: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.displayedColumns = this.getVisibleColumns().map(c => c.key);
    }

    if (changes['data']) {
      this.dataSource.data = this.data || [];
      setTimeout(() => {
        // Always attach sort so client-side sorting works regardless of pagination mode
        if (this.sort) { this.dataSource.sort = this.sort; }
        // Only attach paginator for client-side pagination
        if (!this.pagination && this.paginator) {
          this.dataSource.paginator = this.paginator;
        }
      });
    }

    if (changes['pagination'] && this.pagination && this.paginator) {
      this.paginator.pageIndex = this.pagination.pageIndex;
      this.paginator.length = this.pagination.totalRows;
    }
  }

  ngAfterViewInit(): void {
    // Case-insensitive sorting for string values
    this.dataSource.sortingDataAccessor = (data: any, sortHeaderId: string): string | number => {
      const value = data[sortHeaderId];
      if (typeof value === 'string') {
        return value.toLowerCase();
      }
      return value ?? '';
    };

    // Always attach sort so client-side sorting works regardless of pagination mode
    if (this.sort) { this.dataSource.sort = this.sort; }

    if (!this.pagination) {
      if (this.paginator) { this.dataSource.paginator = this.paginator; }
    } else if (this.paginator) {
      this.paginator.pageIndex = this.pagination.pageIndex;
      this.paginator.length = this.pagination.totalRows;
    }
  }

  /* ───────────── Helpers ───────────── */

  getVisibleColumns(): ColumnConfig[] {
    return (this.columns || []).filter(c => !c.hidden);
  }

  getAlignment(col: ColumnConfig): string {
    switch (col.align) {
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      default: return 'text-left';
    }
  }

  formatCell(row: any, col: ColumnConfig): string {
    const field = col.dataField || col.key;
    const value = row[field];
    if (value == null || value === '') { return '--'; }

    switch (col.type) {
      case 'currency':
        return '$' + this.formatNumber(value, col.currencyFormat || '1.2-2');
      case 'number':
        return this.formatNumber(value, col.numberFormat || '1.0-0');
      case 'date':
        return this.formatDate(value, col.dateFormat || 'MM/dd/yyyy');
      case 'phone':
        return this.formatPhone(String(value));
      default:
        return String(value);
    }
  }

  isSelected(row: any): boolean {
    if (!this.selectedRow || !this.selectionKey) { return false; }
    return row[this.selectionKey] === this.selectedRow[this.selectionKey];
  }

  onRowClick(row: any): void {
    if (this.clickableRows) {
      this.rowClick.emit(row);
    }
  }

  onPageChange(event: PageEvent): void {
    this.pageChange.emit(event);
  }

  onSortChange(sort: Sort): void {
    this.sortChange.emit(sort);
  }

  /* ───────────── Private formatting ───────────── */

  private formatNumber(value: any, format: string): string {
    const num = Number(value);
    if (isNaN(num)) { return String(value); }

    // Parse format like '1.2-2' → minInt=1, minFrac=2, maxFrac=2
    const parts = format.split('.');
    let minFrac = 0;
    let maxFrac = 0;
    if (parts[1]) {
      const fracParts = parts[1].split('-');
      minFrac = parseInt(fracParts[0], 10) || 0;
      maxFrac = parseInt(fracParts[1], 10) || minFrac;
    }
    return num.toLocaleString('en-US', {
      minimumFractionDigits: minFrac,
      maximumFractionDigits: maxFrac,
    });
  }

  private formatDate(value: any, format: string): string {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) { return String(value); }

      // Simple date formatting for common patterns
      const pad = (n: number) => n.toString().padStart(2, '0');
      const month = pad(date.getUTCMonth() + 1);
      const day = pad(date.getUTCDate());
      const year = date.getUTCFullYear();

      // Support common date format patterns
      switch (format) {
        case 'MM/dd/yyyy': return `${month}/${day}/${year}`;
        case 'yyyy-MM-dd': return `${year}-${month}-${day}`;
        case 'dd/MM/yyyy': return `${day}/${month}/${year}`;
        default: return `${month}/${day}/${year}`;
      }
    } catch {
      return String(value);
    }
  }

  private formatPhone(value: string): string {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits[0] === '1') {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return value;
  }

  /** Paginator config helpers */
  get paginatorPageSize(): number {
    return this.pagination?.pageSize || 10;
  }

  get paginatorPageSizeOptions(): number[] {
    return this.pagination?.pageSizeOptions || [10, 25, 50, 100];
  }

  get paginatorLength(): number {
    return this.pagination?.totalRows || 0;
  }

  get paginatorPageIndex(): number {
    return this.pagination?.pageIndex || 0;
  }
}
